import { useEffect, useRef, useState } from 'react'
import type { SessionContext } from '@esign/contracts'

const signedContract = new URL('./assets/signed-contract.png', import.meta.url).href

function formatDate(iso?: string | null): string {
  const d = iso ? new Date(iso) : new Date()
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}

export interface UserContractItem {
  id: number | string
  session_id?: number | string
  contract_ref?: string
  contract_number?: string
  template_code?: string
  status?: string
  core_status?: string
  link?: string | null
  signed_at?: string | null
  sign_expire_at?: string | null
  created_at?: string | null
  signed_contract_url?: string | null
}

export function SignedScreen({
  context,
  initialContracts,
  onSignNew,
}: {
  context: SessionContext
  initialContracts?: UserContractItem[]
  onSignNew?: () => void
}) {
  const [viewMode, setViewMode] = useState<'just_signed' | 'contract_list'>(() => {
    if (typeof sessionStorage !== 'undefined') {
      const isJustSigned = sessionStorage.getItem(`esign_just_signed_${context.session.id}`) === '1'
      if (isJustSigned) {
        sessionStorage.removeItem(`esign_just_signed_${context.session.id}`)
        return 'just_signed'
      }
    }
    return 'contract_list'
  })

  const [contracts, setContracts] = useState<UserContractItem[]>(() => initialContracts || [])
  const [loading, setLoading] = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(null)
  const fetchedRef = useRef(Boolean(initialContracts && initialContracts.length > 0))

  useEffect(() => {
    if (initialContracts && initialContracts.length > 0) {
      setContracts(initialContracts)
      return
    }

    if (fetchedRef.current) return
    fetchedRef.current = true

    const config = typeof window !== 'undefined' ? (window as any).__ESIGN_CONFIG__ : null
    const token =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('dev_webview_token') : null) ||
      config?.MIDDLEWARE_WEBVIEW_TOKEN

    const baseUrl = config?.MIDDLEWARE_API_BASE_URL || 'https://at-product-family-econtract-api.dev.oneat.org'

    const headers: Record<string, string> = {
      Accept: 'application/json',
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    setLoading(true)
    fetch(`${baseUrl}/api/v1/webview/contracts`, {
      headers,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (body && body.status === 'success') {
          const list = body.data?.contracts || []
          setContracts(list)
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false)
      })
  }, [initialContracts])

  // Danh sách hợp đồng hiển thị (nếu API rỗng thì fallback theo session hiện tại)
  const displayContracts: UserContractItem[] = contracts.length > 0 ? contracts : [
    {
      id: context.session.id || '140',
      contract_ref: context.legal.template_id || 'EKYCC_140',
      contract_number: context.legal.template_id || 'EKYCC_140',
      created_at: new Date().toISOString(),
      core_status: 'WAITING_TO_SIGN',
      status: 'pending',
      link: null,
    }
  ]

  // Xử lý thoát / quay về
  const handleExit = () => {
    // 1. Gửi event lên React Native WebView nếu đang chạy trong App
    try {
      if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'esign:exit',
            status: 'signed',
            session_id: context.session.id,
          })
        )
      }
    } catch (err) {
      console.warn('ReactNativeWebView postMessage error:', err)
    }

    // 2. Điều hướng theo cấu hình container exit mặc định
    const { exit } = context.container
    if (exit.type === 'redirect') {
      location.assign(exit.target)
    } else if (exit.type === 'deeplink') {
      location.href = exit.target
    } else {
      location.assign(exit.target)
    }
  }

  const getStatusBadge = (item: UserContractItem) => {
    const st = (item.core_status || item.status || '').toUpperCase()
    if (st === 'SIGNED' || st === 'COMPLETED' || st === 'HOAN_THANH' || st === 'DA_KY') {
      return <span className="scalef-badge scalef-badge--success">ĐÃ KÝ</span>
    }
    if (st === 'WAITING_TO_SIGN' || st === 'PENDING' || st === 'CHO_KY' || st === 'SUBMIT') {
      return <span className="scalef-badge scalef-badge--warning">CHỜ KÝ</span>
    }
    if (st === 'OVERDUE' || st === 'REVOKED' || st === 'EXPIRED' || st === 'HET_HAN') {
      return <span className="scalef-badge scalef-badge--danger">HẾT HẠN</span>
    }
    if (st === 'REJECT' || st === 'REJECTED' || st === 'TU_CHOI' || st === 'SUBMIT_FAIL' || st === 'FAIL') {
      return <span className="scalef-badge scalef-badge--danger">TỪ CHỐI</span>
    }
    if (st === 'VOIDED' || st === 'CANCELLED' || st === 'DELETED' || st === 'DA_HUY') {
      return <span className="scalef-badge scalef-badge--danger">ĐÃ HỦY</span>
    }
    if (st === 'PROCESSING' || st === 'IN_PROGRESS' || st === 'DRAFT' || st === 'DANG_XU_LY') {
      return <span className="scalef-badge scalef-badge--info">ĐANG XỬ LÝ</span>
    }
    return <span className="scalef-badge scalef-badge--warning">{st || 'CHỜ KÝ'}</span>
  }

  const canSignNew = displayContracts.length === 0 || displayContracts.every(contract => {
    const st = (contract.core_status || contract.status || '').toUpperCase()
    return ['SUBMIT_FAIL', 'FAIL', 'REJECT', 'REJECTED', 'OVERDUE', 'EXPIRED', 'OTHER', 'VOIDED', 'CANCELLED', 'DELETED', 'HET_HAN', 'TU_CHOI', 'DA_HUY'].includes(st)
  })

  return (
    <div className="scalef-signed-wrapper">

      {viewMode === 'just_signed' ? (
        <>
          {/* Card thành công */}
          <div className="scalef-success-card">
            <div className="scalef-success-icon-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 className="scalef-success-title">Ký hợp đồng thành công</h2>
            <p className="scalef-success-subtitle">
              Thông tin hợp đồng đã được lưu vào hệ thống
            </p>

            <div className="scalef-status-group">
              <div className="scalef-status-item">
                <span style={{ color: '#16A34A', fontWeight: 'bold' }}>✓</span> Xác minh danh tính: Hoàn thành
              </div>
              <div className="scalef-status-item">
                <span style={{ color: '#16A34A', fontWeight: 'bold' }}>✓</span> Ký hợp đồng điện tử: Hoàn thành
              </div>
              <div className="scalef-status-item">
                <span style={{ color: '#16A34A', fontWeight: 'bold' }}>✓</span> Email xác nhận đã được gửi
              </div>
            </div>

            <div className="scalef-actions-row">
              <button
                type="button"
                className="scalef-btn-home"
                style={{ width: '100%', height: '44px', borderRadius: '8px' }}
                onClick={() => setViewMode('contract_list')}
              >
                Xem danh sách hợp đồng đã ký
              </button>
              <button
                type="button"
                className="scalef-btn-secondary"
                onClick={handleExit}
              >
                Quay lại ứng dụng
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Khối trên: Tất cả đã sẵn sàng */}
          <div className="scalef-ready-card">
            <div className="scalef-ready-icon-badge">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 className="scalef-ready-title">Tất cả đã sẵn sàng</h2>
            <p className="scalef-ready-subtitle">
              Bạn sẽ nhận được thanh toán <strong>sau khi hợp đồng được duyệt</strong>
            </p>
          </div>

          {/* Khối dưới: Danh sách hợp đồng đã ký */}
          <div className="scalef-contract-section">
            <div className="scalef-contract-section-head">
              <h3 className="scalef-contract-section-title">Danh sách hợp đồng đã ký</h3>
              {onSignNew && canSignNew ? (
                <div className="scalef-sign-new-row">
                  <button
                    type="button"
                    className="scalef-btn-secondary scalef-btn-sign-new"
                    onClick={onSignNew}
                  >
                    Ký hợp đồng mới
                  </button>
                </div>
              ) : null}
            </div>

            <div className="scalef-table-container">
              <table className="scalef-table">
                <thead>
                  <tr>
                    <th>Tài liệu</th>
                    <th>Ngày kí hợp đồng</th>
                    <th>Trạng thái</th>
                    <th style={{ textAlign: 'center' }}>Hành động</th>
                    <th>Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748B' }}>
                        Đang tải danh sách hợp đồng…
                      </td>
                    </tr>
                  ) : displayContracts.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748B' }}>
                        Chưa có hợp đồng nào
                      </td>
                    </tr>
                  ) : (
                    displayContracts.map((contract) => {
                      const itemCode = contract.contract_ref || contract.contract_number || `EKYCC_${contract.id}`
                      const itemDate = formatDate(contract.signed_at || contract.created_at)
                      const itemLink = contract.link || contract.signed_contract_url

                      return (
                        <tr key={String(contract.id)}>
                          <td className="scalef-table__code">{itemCode}</td>
                          <td>{itemDate}</td>
                          <td>{getStatusBadge(contract)}</td>
                          <td style={{ textAlign: 'center' }}>
                            {itemLink ? (
                              <a
                                href={itemLink}
                                target="_blank"
                                rel="noreferrer"
                                className="scalef-icon-btn"
                                title="Ký / Xem hợp đồng"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFFFFF">
                                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                </svg>
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="scalef-icon-btn"
                                onClick={() => {
                                  setSelectedPreviewUrl(signedContract)
                                  setShowPdfModal(true)
                                }}
                                title="Xem trước hợp đồng"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFFFFF">
                                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                </svg>
                              </button>
                            )}
                          </td>
                          <td>
                            {contract.sign_expire_at && (contract.core_status === 'WAITING_TO_SIGN' || contract.status === 'pending')
                              ? `Hạn ký: ${formatDate(contract.sign_expire_at)}`
                              : ''}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal xem tài liệu nếu chưa có link trực tiếp */}
      {showPdfModal && (
        <div className="scalef-modal-backdrop" onClick={() => setShowPdfModal(false)}>
          <div className="scalef-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="scalef-modal-header">
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Bản xem trước hợp đồng</h4>
              <button
                type="button"
                style={{ background: 'none', border: 0, fontSize: '18px', cursor: 'pointer', color: '#64748B' }}
                onClick={() => setShowPdfModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="scalef-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '12px' }}>
              <img
                src={selectedPreviewUrl || signedContract}
                alt="Hợp đồng"
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

